import { useCallback, useEffect, useState } from 'react';

import { useMatch, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useGetExportDownloadUrl } from '@/api/mutations/useGetExportDownloadUrl';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';

interface ExportReadyDialogProps {
  /** Optional message to display in the dialog body. */
  message?: string;
  /** Optional title for the dialog. */
  dialogTitle?: string;
}

/**
 * Self-contained download dialog. Reads `?exportId=` from the URL on mount,
 * opens the dialog, and resolves the download URL via the unified backend
 * endpoint when the user clicks Download. Safe to mount once at the app root.
 */
export function ExportReadyDialog({
  message = 'Your download is ready.',
  dialogTitle = 'Download Ready',
}: ExportReadyDialogProps = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const matterMatch = useMatch('/matters/:matterId/*');
  const matterId = matterMatch?.params.matterId ?? null;
  const [isOpen, setIsOpen] = useState(false);
  const [exportId, setExportId] = useState<string | null>(null);

  const { mutateAsync: getDownloadUrl, isLoading: isDownloading } = useGetExportDownloadUrl();

  const exportIdParam = searchParams.get('exportId');
  useEffect(() => {
    if (!exportIdParam) return;
    // Watch the param (not mount-only) so in-app flows can hand off to this
    // dialog after it has mounted by setting `?exportId=`. Strip it once read.
    setExportId(exportIdParam);
    setIsOpen(true);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('exportId');
    setSearchParams(newSearchParams, { replace: true });
  }, [exportIdParam]);

  const handleDownload = useCallback(async () => {
    if (!exportId) return;
    if (!matterId) {
      // The download endpoint is matter-scoped; every export link lands on a
      // /matters/:matterId/* page, so this only fires if the dialog is opened
      // from an unexpected route.
      toast.error('Open this download link from its matter page to download the export.');
      return;
    }
    try {
      const { downloadUrl, fileName } = await getDownloadUrl({ matterId, exportId });
      const link = Object.assign(document.createElement('a'), {
        href: downloadUrl,
        download: fileName,
      });
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setIsOpen(false);
      setExportId(null);
    } catch (error) {
      const detail = error instanceof Error ? error.message : null;
      toast.error(detail || 'Failed to download export. Please try again, or contact support@wexler.ai');
    }
  }, [exportId, matterId, getDownloadUrl]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setExportId(null);
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--download" />
            {dialogTitle}
          </DialogTitle>
        </DialogHeader>
        <p>{message}</p>
        <DialogFooter>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleDownload} variant="primary" disabled={isDownloading}>
            {isDownloading ? 'Downloading...' : 'Download'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
